import dayjs from 'dayjs';
import { isUndefined } from 'lodash';
import { useMemo } from 'react';
import { DATETIME_FORMAT_DATETIME } from '../../../../../utils/helpers/CommonHelper.jsx';

export function useParsePreviewData(previewData) {
  return useMemo(() => {
    const areThereAnyValues = (previewData || []).some(
      (attribute) => (attribute?.sensor_values || []).length > 0,
    );

    if (isUndefined(previewData) || !areThereAnyValues) {
      return { datasets: {}, labels: [], minDate: dayjs(), maxDate: dayjs() };
    }

    const { minDate, maxDate } =
      findMinAndMaxDateAmongAllAttributes(previewData);

    const attributesWithValuesKeyedByCreatedAt =
      keyByAttributesSensorValuesCreatedAt(previewData);

    const datasets = {};
    const labels = [];

    previewData.forEach((attribute) => {
      datasets[attribute.id] = [];
    });

    /**
     * while loop must be used to replace nonexistent values with null, to break line in chart
     *
     * Principe:
     * iterate while currenDate is less than maxDate (go though every value from START to END of axis)
     * check if currentDate exists in attributesWithValuesKeyedByCreatedAt
     * if value exists insert it to dataset object
     * if value doesn't exists, insert null instead,
     *
     * This way line in graph is broken, to represent time, when value does not exists
     */
    let currentDate = minDate;
    while (currentDate < maxDate) {
      currentDate = currentDate.add(20, 'minute');
      const date = currentDate.format(DATETIME_FORMAT_DATETIME);
      const milliseconds = currentDate.valueOf();

      // use current date as label
      labels.push(date);

      attributesWithValuesKeyedByCreatedAt.forEach((attribute) => {
        const valueAtDate = attribute.sensor_values[date];

        datasets[attribute.id].push({
          x: milliseconds,
          y: !isUndefined(valueAtDate) ? valueAtDate.value : null,
        });
      });
    }

    return { datasets, labels, minDate, maxDate };
  }, [previewData]);
}

/**
 * Method finds min and max date from all attributes from previewData object
 * minDate & maxDate represent START and END of X axis
 *
 * @param {array} previewData
 * @returns {object}
 */
function findMinAndMaxDateAmongAllAttributes(previewData) {
  let minDate = dayjs();
  let maxDate = dayjs(new Date(0));

  previewData.forEach((attribute) => {
    if (isUndefined(attribute?.sensor_values)) {
      return;
    }

    const currentMinDate = dayjs(attribute.sensor_values[0].created_at);
    const currentMaxDate = dayjs(
      attribute.sensor_values[attribute.sensor_values.length - 1].created_at,
    );

    if (currentMinDate < minDate) {
      minDate = currentMinDate;
    }

    if (currentMaxDate > maxDate) {
      maxDate = currentMaxDate;
    }
  });

  return { minDate, maxDate };
}

/**
 * * Creates hashMap of sensor values, where key is formatted created_at date of current value
 *
 * ? Structure:
 *
 * attribute:{
 *    ...attribute,
 *    sensor_values: {
 *      '21.3. 2024 10:00': { ...sensorValue }
 *    }
 * }
 *
 * @param {array} previewData
 * @returns {object}
 */
function keyByAttributesSensorValuesCreatedAt(previewData) {
  return previewData.map((attribute) => {
    const sensorValuesObj = {};

    (attribute?.sensor_values || []).forEach((sensorValue) => {
      sensorValuesObj[
        dayjs(sensorValue.created_at).format(DATETIME_FORMAT_DATETIME)
      ] = sensorValue;
    });

    return {
      ...attribute,
      sensor_values: sensorValuesObj,
    };
  });
}
